
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState } from "react";
import { PurchaseOrderDialog } from "../purchase-order-v2/PurchaseOrderDialog";

interface BankTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  amount: number;
  bankIban: string;
  campaignId: string;
  companyDetails: any;
  devices: number;
  duration: number;
}

const BankTransferDialog = ({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  amount,
  bankIban,
  campaignId,
  companyDetails,
  devices,
  duration
}: BankTransferDialogProps) => {
  const { t, language } = useLanguage();
  const [showPurchaseOrder, setShowPurchaseOrder] = useState(false);
  const [understood, setUnderstood] = useState(false);

  const handleConfirm = () => {
    console.log("Bank transfer dialog: Showing purchase order without confirming payment");
    setShowPurchaseOrder(true);
    onOpenChange(false);
    // Removed the onConfirm() call which was triggering campaign submission
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={`sm:max-w-lg ${language === 'ar' ? 'text-right' : ''} relative fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50`}>
          <button 
            onClick={() => onOpenChange(false)}
            className="absolute right-2 top-2 p-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M18 6L6 18"></path>
              <path d="M6 6l12 12"></path>
            </svg>
            <span className="sr-only">Close</span>
          </button>
          <DialogHeader>
            <DialogTitle className={`text-xl font-semibold ${language === 'ar' ? 'text-right' : ''} pr-8`}>
              {language === 'ar' ? 'تأكيد إنشاء أمر شراء' : 'Confirm Purchase Order Generation'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <p className="text-gray-700 mb-6">
              {language === 'ar' 
                ? 'هل أنت متأكد من أنك تريد إنشاء أمر شراء؟ سيتم إرسال أمر الشراء إلى بريدك الإلكتروني.' 
                : 'Are you sure you want to generate a purchase order? The purchase order will be sent to your email.'}
            </p>
            
            {/* Read and understand checkbox */}
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30 mb-6">
              <Checkbox
                id="bank-transfer-understood"
                checked={understood}
                onCheckedChange={(checked) => setUnderstood(checked === true)}
                className="mt-0.5"
              />
              <Label htmlFor="bank-transfer-understood" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
                {language === 'ar' 
                  ? 'لقد قرأت وفهمت أن حملتي ستبدأ فقط بعد تأكيد استلام الدفع.'
                  : 'I have read and understand that my campaign will only start after the payment is confirmed as received.'
                }
              </Label>
            </div>

            <div className={`flex gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''} justify-end`}>
              <Button variant="outline" onClick={onCancel}>
                {t('cancel')}
              </Button>
              <Button onClick={handleConfirm} disabled={!understood}>
                {language === 'ar' ? 'إنشاء أمر شراء' : 'Generate Purchase Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PurchaseOrderDialog 
        open={showPurchaseOrder}
        onOpenChange={setShowPurchaseOrder}
        onComplete={onConfirm} 
        campaignId={campaignId}
        companyDetails={companyDetails}
        devices={devices}
        duration={duration}
        amount={amount}
      />
    </>
  );
};

export default BankTransferDialog;
